import React, { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import backgroundImage from "../assets/images/schedule.png";
import ValidatedTextField from "../components/shared/inputs/ValidatedTextField";
import {
  emailValidator,
  passwordValidator,
} from "../helpers/validators/input-validators";
import { Link, useNavigate } from "react-router-dom";
import { SCHEDULING_URL, REGISTER_URL } from "../constants/routeConstants";
import { LOCALSTORAGE_USER } from "../constants/localStorageConstants";
import { useDispatch } from "react-redux";
import { authActions } from "../store/slices/authSlice";
import axios from "axios";
import { useSnackbar } from "notistack";
import { LockIcon } from "lucide-react";

export default function SignInSide() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // lock body scroll while on login page
  useLockBodyScroll();

  const handleLoginSuccess = (data) => {
    localStorage.setItem(LOCALSTORAGE_USER, JSON.stringify(data));
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    enqueueSnackbar("Successfully logged in!", { variant: "success" });
    navigate(SCHEDULING_URL);
  };

  const handleLoginError = (error) => {
    enqueueSnackbar(error, { variant: "error" });
  };

  const checkInputsLength = email.length === 0 || password.length === 0;

  const loginHandler = () => {
    if (emailError || passwordError) return;
    dispatch(
      authActions.loginFetch({
        email,
        password,
        handleLoginSuccess,
        handleLoginError,
      })
    );
  };

  return (
    <Grid
      container
      component="main"
      spacing={0}
      sx={{
        height: '100vh',
        minHeight: { xs: '100vh', md: '100vh' },
        margin: { xs: 0, md: '-20px' },
        overflow: { xs: 'hidden', md: 'visible' },
      }}
    >
      <CssBaseline />
      <Grid
        item
        md={7}
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: "relative",
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) =>
            t.palette.mode === "light"
              ? t.palette.grey[50]
              : t.palette.grey[900],
          backgroundSize: "cover",
          backgroundPosition: "center",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
        }}
      >
        {/* Tamni overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.6)",
            zIndex: 1,
          }}
        />

        {/* Tekst */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "left",
            top: 5,
            left: 5,
            px: 4,
            maxWidth: 520,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: 22, md: 34 } }}>
            Automatizujte vaše putovanje
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: 13, md: 18 } }}>
            Generišite rute, formirajte grupe i rezervišite u par klikova.
          </Typography>
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        md={5}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          px: { xs: 2, sm: 4 },
          py: { xs: 6, sm: 8 }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Avatar sx={{ m: 1, backgroundColor: "transparent", width: { xs:56, sm:64 }, height: { xs:56, sm:64 } }}>
            <LockIcon color="#636AE8" />
          </Avatar>
          <Typography fontSize={{ xs: 22, sm: 30 }} sx={{ fontWeight: "bold", mb: 1 }}>
            Prijava
          </Typography>
          <Box component="form" noValidate sx={{ mt: 1, width: { xs: '100%', sm: 420 } }}>
            <Typography sx={{ fontSize: "14px" }}>Email *</Typography>
            <ValidatedTextField
              value={email}
              setValue={setEmail}
              error={emailError}
              setError={setEmailError}
              label="test@primer.com"
              validator={emailValidator}
              fullWidth
              type="email"
              sx={{ mb: 1, borderRadius: '6px' }}
            />
            <Typography sx={{ fontSize: "14px" }}>Lozinka *</Typography>
            <ValidatedTextField
              value={password}
              setValue={setPassword}
              error={passwordError}
              setError={setPasswordError}
              label="********"
              validator={passwordValidator}
              fullWidth
              type="password"
            />
            <Button
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2, backgroundColor: "#636AE8", py: 1.5 }}
              onClick={loginHandler}
              disabled={checkInputsLength}
            >
              Prijavite se
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                fontSize: "12px",
              }}
            >
              <Typography
                sx={{
                  color: "black",
                  textDecoration: "none",
                  fontSize: "12px",
                }}
              >
                Nemate nalog?{" "}
                <Link
                  to={REGISTER_URL}
                  style={{
                    textDecoration: "none",
                    color: "#636AE8",
                    fontWeight: 500,
                  }}
                >
                  Registrujte se
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}

// Prevent body scroll while Login page is mounted (restores previous overflow on unmount)
// Note: this will completely disable scrolling on the entire page while this component is active.
// Make sure content fits the viewport on small devices, otherwise users won't be able to access clipped content.
export function useLockBodyScroll() {
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);
}
